import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Checkbox,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import { raidApi } from "../../services/supabaseApi";
import { entityApi } from "../../services/entityApi";
import { raidAuthoringApi } from "../../services/raidAuthoringApi";
import { bossRoomsApi } from "../../services/bossRoomsApi";
import { entityStats } from "../../utils/entityStats";
import { RaidEntity } from "../../types";
import {
  RaidRoom,
  entityCategory,
  isBoss,
  validateRoom,
} from "../../utils/raidAuthoring";
import { useApp } from "../../contexts/AppContext";
import SimpleMapModal from "../SimpleMapModal";
import "./ResourceTables.css";

const { Title, Text } = Typography;
const required = [{ required: true, message: "값을 입력해주세요." }];
const options = (rows: any[], key: string, name: string) =>
  rows.map((r) => ({ value: r[key], label: r[name] + " (" + r[key] + ")" }));

const RaidWorkspace: React.FC = () => {
  const { selectEntity, selectBoss, setActiveTab } = useApp();
  const [raids, setRaids] = useState<any[]>([]),
    [rooms, setRooms] = useState<any[]>([]),
    [entities, setEntities] = useState<RaidEntity[]>([]);
  const [assignments, setAssignments] = useState<RaidRoom[]>([]);
  const [raidCode, setRaidCode] = useState<string>(),
    [tab, setTab] = useState("rooms"),
    [filter, setFilter] = useState("전체"),
    [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false),
    [saving, setSaving] = useState(false),
    [error, setError] = useState(""),
    [ready, setReady] = useState(false);
  const [editor, setEditor] = useState<{
    kind: "raid" | "room" | "entity";
    record?: any;
  } | null>(null);
  const [form] = Form.useForm();
  const [compared, setCompared] = useState<React.Key[]>([]),
    [map, setMap] = useState<any>(null),
    [maps, setMaps] = useState<Record<string, any[]>>({});
  const [combatStats, setCombatStats] = useState<Record<string, number | null>>(
    {},
  );
  const [newStat, setNewStat] = useState<string>();
  const [memberSearch, setMemberSearch] = useState("");
  const [showAllStats, setShowAllStats] = useState(false);
  const load = async () => {
    setLoading(true);
    setReady(false);
    setError("");
    try {
      const [r, e, b] = await Promise.all([
        raidApi.getAll(),
        entityApi.getAll(),
        raidApi.getBossRooms(),
      ]);
      setRaids(r);
      setEntities(e);
      setRooms(b);
      setRaidCode((current) =>
        r.some((x) => x.raid_code === current) ? current : r[0]?.raid_code,
      );
      try {
        setAssignments(await raidAuthoringApi.rooms());
        setReady(true);
      } catch (e: any) {
        setReady(false);
        setError(
          "룸 연결 데이터를 불러오지 못했습니다. DB 마이그레이션 또는 접근 권한을 확인해주세요. " +
            e.message,
        );
      }
      const loaded = await Promise.all(
        b.map(async (room) => [
          room.room_code,
          await bossRoomsApi.getByRoomCode(room.room_code),
        ]),
      );
      setMaps(Object.fromEntries(loaded));
    } catch (e: any) {
      setError(e.message || "데이터 조회 실패");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);
  const raid = raids.find((r) => r.raid_code === raidCode);
  const members = entities.filter((e) => e.spawn_raid_code === raidCode);
  const raidRooms = assignments.filter((r) => r.raid_code === raidCode);
  const repCodes = raidRooms.flatMap((r) => r.representative_codes);
  const unconfigured = Array.from(
    new Set(
      [raid?.raid_room_code, ...members.map((e) => e.spawn_room_code)].filter(
        Boolean,
      ),
    ),
  ).filter((code) => !raidRooms.some((r) => r.room_code === code));
  const nameOf = (code: string) =>
    rooms.find((r) => r.room_code === code)?.room_name || code;
  const open = (kind: "raid" | "room" | "entity", record?: any) => {
    form.resetFields();
    setNewStat(undefined);
    form.setFieldsValue(
      kind === "raid"
        ? { dungeon_x: 0, dungeon_y: 0, min_entry_level: 1, ...record }
        : kind === "room"
          ? {
              room_order: raidRooms.length + 1,
              representative_codes: [],
              ...rooms.find((r) => r.room_code === record?.room_code),
              ...record,
            }
          : {
              entity_type: "BOSS",
              phase_count: 1,
              size_tiles: 1,
              spawn_order: 1,
              balance_status: "unreviewed",
              game_version: "1.0.0",
              ...record,
            },
    );
    setCombatStats(
      Object.fromEntries(
        entityStats.map(({ key }) => [key, record?.[key] ?? null]),
      ),
    );
    setEditor({ kind, record });
  };
  const save = async () => {
    setSaving(true);
    try {
      if (!ready) throw Error("연결 데이터를 먼저 불러와주세요.");
      const values = await form.validateFields();
      if (editor?.kind === "raid") {
        const data = {
          ...values,
          raid_room_code: values.raid_room_code || null,
        };
        if (editor.record) await raidApi.update(editor.record.raid_code, data);
        else await raidApi.create(data);
        setRaidCode(values.raid_code);
      } else if (editor?.kind === "room") {
        const assignment = {
          raid_code: raidCode!,
          room_code: values.room_code,
          room_order: values.room_order,
          representative_codes: values.representative_codes,
        };
        const problem = validateRoom(assignment, entities);
        if (problem) throw Error(problem);
        await raidAuthoringApi.saveRoom(
          assignment,
          values.room_name,
          values.description || "",
        );
      } else if (editor?.kind === "entity") {
        const data = {
          ...values,
          spawn_raid_code: raidCode,
          spawn_room_code: values.spawn_room_code || null,
          parent_code: values.parent_code || null,
          ...combatStats,
        };
        if (
          data.balance_status === "approved" &&
          (!data.max_hp ||
            entityStats.some(({ key }) => combatStats[key] == null))
        )
          throw Error("검토 완료 상태에는 HP와 모든 능력치가 필요합니다.");
        if (data.parent_code === data.entity_code)
          throw Error("자기 자신을 관련 보스로 지정할 수 없습니다.");
        const affected = raidRooms.filter((r) =>
          r.representative_codes.includes(data.entity_code),
        );
        if (
          affected.some(
            (r) => r.room_code !== data.spawn_room_code || !isBoss(data),
          )
        )
          throw Error(
            "룸의 대표 보스입니다. 룸에서 대표 보스를 변경한 뒤 이동하거나 분류를 변경해주세요.",
          );
        if (editor.record)
          await entityApi.update(editor.record.entity_code, data);
        else await entityApi.create(data);
      }
      setEditor(null);
      window.dispatchEvent(new Event("raidDataUpdated"));
      await load();
      message.success("저장했습니다.");
    } catch (e: any) {
      message.error(e.message || "필수 입력값을 확인해주세요.");
    } finally {
      setSaving(false);
    }
  };
  const openDetails = (
    e: RaidEntity,
    section: "basic" | "skills" | "triggers-v3",
  ) => {
    selectEntity(e);
    setActiveTab(section);
  };
  const removeEntity = async (e: RaidEntity) => {
    try {
      if (repCodes.includes(e.entity_code))
        throw Error("대표 보스를 먼저 변경해주세요.");
      await entityApi.delete(e.entity_code);
      await load();
      window.dispatchEvent(new Event("raidDataUpdated"));
    } catch (e: any) {
      message.error(e.message);
    }
  };
  const entityColumns: any[] = [
    {
      title: "몬스터",
      dataIndex: "entity_name",
      render: (name: string, e: RaidEntity) => (
        <Space direction="vertical">
          <Button type="link" onClick={() => openDetails(e, "basic")}>
            {name}
          </Button>
        </Space>
      ),
    },
    {
      title: "구분",
      render: (_: any, e: RaidEntity) => (
        <Space>
          <Tag
            color={
              isBoss(e)
                ? "gold"
                : entityCategory(e) === "쫄몹"
                  ? "cyan"
                  : "default"
            }
          >
            {entityCategory(e)}
          </Tag>
          {repCodes.includes(e.entity_code) && <Tag color="green">룸 대표</Tag>}
        </Space>
      ),
    },
    {
      title: "출현 룸",
      dataIndex: "spawn_room_code",
      render: (code: string) => (code ? nameOf(code) : <Tag>미배치</Tag>),
    },
    {
      title: "관련 보스",
      dataIndex: "parent_code",
      render: (code: string) =>
        entities.find((e) => e.entity_code === code)?.entity_name ||
        code ||
        "—",
    },
    {
      title: "능력치 상태",
      dataIndex: "balance_status",
      render: (v: string) => (
        <Tag color={v === "approved" ? "green" : "orange"}>
          {v === "approved"
            ? "검토 완료"
            : v === "draft"
              ? "작성 중"
              : "미검토"}
        </Tag>
      ),
    },
    {
      title: "HP",
      dataIndex: "max_hp",
      render: (hp: number) => (hp == null ? "미입력" : hp.toLocaleString()),
    },
    {
      title: "관리",
      render: (_: any, e: RaidEntity) => (
        <Space wrap>
          <Button onClick={() => open("entity", e)}>편집</Button>
          <Button onClick={() => openDetails(e, "skills")}>스킬</Button>
          <Button onClick={() => openDetails(e, "triggers-v3")}>트리거</Button>
          <Popconfirm
            title="몬스터를 삭제할까요?"
            description="연결된 데이터가 있으면 삭제가 거부될 수 있습니다."
            onConfirm={() => removeEntity(e)}
          >
            <Button danger disabled={repCodes.includes(e.entity_code)}>
              삭제
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];
  const selected = members.filter((e) => compared.includes(e.entity_code));
  const statKeys = showAllStats
    ? entityStats
    : entityStats.filter((s) =>
        selected.some((e) => (e as any)[s.key] != null),
      );
  const comparison = (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Alert
        type="info"
        message="같은 레이드의 보스·쫄몹을 선택해 HP와 입력된 능력치를 비교합니다. 미입력은 0과 구분합니다."
      />
      <Select
        aria-label="비교할 몬스터"
        mode="multiple"
        style={{ width: "100%" }}
        placeholder="비교할 몬스터 선택"
        value={compared as string[]}
        options={options(
          members.filter((e) => entityCategory(e) !== "오브젝트"),
          "entity_code",
          "entity_name",
        )}
        onChange={setCompared}
      />
      <Checkbox
        checked={showAllStats}
        onChange={(e) => setShowAllStats(e.target.checked)}
      >
        미입력 능력치도 표시
      </Checkbox>
      {!selected.length ? (
        <Empty description="비교할 몬스터를 선택해주세요" />
      ) : (
        <Table
          pagination={false}
          rowKey="key"
          scroll={{ x: true }}
          dataSource={[{ key: "HP", name: "HP" }, ...statKeys]}
          columns={[
            { title: "능력치", dataIndex: "name" },
            ...selected.map((e) => ({
              title: e.entity_name,
              key: e.entity_code,
              render: (_: any, row: any) => {
                const n = row.key === "HP" ? e.max_hp : (e as any)[row.key];
                return n == null ? "미입력" : Number(n).toLocaleString();
              },
            })),
          ]}
        />
      )}
    </Space>
  );
  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
        <div>
          <Title level={3}>레이드 구성 관리</Title>
          <Text type="secondary">레이드 · 장소(룸) · 보스 · 쫄몹</Text>
        </div>
        <Space>
          <Button onClick={load} loading={loading}>
            새로고침
          </Button>
          <Button type="primary" disabled={!ready} onClick={() => open("raid")}>
            레이드 추가
          </Button>
        </Space>
      </Space>
      {error && (
        <Alert
          style={{ marginTop: 16 }}
          type="error"
          showIcon
          message={error}
        />
      )}
      <Row gutter={[20, 20]} style={{ marginTop: 20 }}>
        <Col xs={24} xl={6}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Input.Search
              placeholder="레이드 검색"
              onChange={(e) => setSearch(e.target.value)}
            />
            {raids
              .filter((r) =>
                (r.raid_name + " " + r.raid_code)
                  .toLowerCase()
                  .includes(search.toLowerCase()),
              )
              .map((r) => (
                <Card
                  key={r.raid_code}
                  size="small"
                  style={{
                    borderColor:
                      raidCode === r.raid_code ? "#1677ff" : undefined,
                  }}
                >
                  <Button
                    type="link"
                    onClick={() => {
                      setRaidCode(r.raid_code);
                      setCompared([]);
                    }}
                  >
                    {r.raid_name}
                  </Button>
                  <div>
                    <Tag>Lv.{r.min_entry_level}</Tag>
                    <Text>
                      {
                        assignments.filter((a) => a.raid_code === r.raid_code)
                          .length
                      }
                      개 룸
                    </Text>
                  </div>
                </Card>
              ))}
          </Space>
        </Col>
        <Col xs={24} xl={18}>
          {raid ? (
            <>
              <Space wrap>
                <Title level={4} style={{ margin: 0 }}>
                  {raid.raid_name}
                </Title>
                <Tag>{members.filter(isBoss).length} 보스</Tag>
                <Tag>
                  {members.filter((e) => entityCategory(e) === "쫄몹").length}{" "}
                  쫄몹
                </Tag>
                <Button disabled={!ready} onClick={() => open("raid", raid)}>
                  레이드 편집
                </Button>
                <Popconfirm
                  title="비어 있는 레이드를 삭제할까요?"
                  onConfirm={async () => {
                    try {
                      await raidApi.delete(raidCode!);
                      await load();
                      window.dispatchEvent(new Event("raidDataUpdated"));
                    } catch (e: any) {
                      message.error(e.message);
                    }
                  }}
                >
                  <Button
                    danger
                    disabled={
                      !ready || members.length > 0 || raidRooms.length > 0
                    }
                  >
                    레이드 삭제
                  </Button>
                </Popconfirm>
              </Space>
              {(unconfigured.length > 0 || !raidRooms.length) && (
                <Alert
                  style={{ marginTop: 16 }}
                  type="warning"
                  showIcon
                  message="룸 구성을 완료해주세요"
                  description={
                    unconfigured.length
                      ? "대표 보스 지정이 필요한 룸: " +
                        unconfigured.map(nameOf).join(", ")
                      : "몬스터를 등록한 뒤 룸에 대표 보스를 지정하세요."
                  }
                />
              )}
              <Tabs
                activeKey={tab}
                onChange={setTab}
                items={[
                  {
                    key: "rooms",
                    label: "장소(룸)",
                    children: (
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <Button
                          type="primary"
                          disabled={!ready}
                          onClick={() => open("room")}
                        >
                          룸 추가·연결
                        </Button>
                        <Collapse
                          style={{ width: "100%" }}
                          items={raidRooms.map((r) => ({
                            key: r.room_code,
                            label: (
                              <Space wrap>
                                <Text strong>
                                  {r.room_order}. {nameOf(r.room_code)}
                                </Text>
                                <Text type="secondary">
                                  몬스터{" "}
                                  {
                                    members.filter(
                                      (e) => e.spawn_room_code === r.room_code,
                                    ).length
                                  }
                                  명
                                </Text>
                                {r.representative_codes.map((code) => (
                                  <Tag key={code} color="gold">
                                    대표:{" "}
                                    {entities.find(
                                      (e) => e.entity_code === code,
                                    )?.entity_name || code}
                                  </Tag>
                                ))}
                              </Space>
                            ),
                            children: (
                              <Card
                                key={r.room_code}
                                title={
                                  r.room_order + ". " + nameOf(r.room_code)
                                }
                                extra={
                                  <Space>
                                    <Button
                                      disabled={!ready}
                                      onClick={() => open("room", r)}
                                    >
                                      룸 편집
                                    </Button>
                                    <Popconfirm
                                      title="이 레이드에서 룸 연결을 해제할까요?"
                                      description="몬스터와 맵은 보존되며, 이 룸의 몬스터는 미배치 상태가 됩니다. 입장 룸이면 입장 룸 지정도 해제됩니다."
                                      onConfirm={async () => {
                                        try {
                                          await raidAuthoringApi.removeRoom(
                                            raidCode!,
                                            r.room_code,
                                          );
                                          await load();
                                        } catch (e: any) {
                                          message.error(e.message);
                                        }
                                      }}
                                    >
                                      <Button disabled={!ready}>
                                        연결 해제
                                      </Button>
                                    </Popconfirm>
                                  </Space>
                                }
                              >
                                <Space wrap>
                                  {r.representative_codes.map((code) => (
                                    <Tag key={code} color="gold">
                                      대표:{" "}
                                      {entities.find(
                                        (e) => e.entity_code === code,
                                      )?.entity_name || code}
                                    </Tag>
                                  ))}
                                  <Button
                                    onClick={() => {
                                      selectBoss(null);
                                      setActiveTab("drop-item-management");
                                    }}
                                  >
                                    보상 관리
                                  </Button>
                                </Space>
                                <p>
                                  <Text type="secondary">
                                    {r.room_code} ·{" "}
                                    {
                                      members.filter(
                                        (e) =>
                                          e.spawn_room_code === r.room_code,
                                      ).length
                                    }
                                    개 배치
                                  </Text>
                                </p>
                                <Space wrap>
                                  <Button
                                    onClick={() =>
                                      setMap({
                                        roomCode: r.room_code,
                                        roomName: nameOf(r.room_code),
                                        mapData: null,
                                      })
                                    }
                                  >
                                    맵 생성
                                  </Button>
                                  {(maps[r.room_code] || []).map((m) => (
                                    <Button
                                      key={m.id}
                                      onClick={() =>
                                        setMap({
                                          roomCode: r.room_code,
                                          roomName: nameOf(r.room_code),
                                          mapId: m.id,
                                          mapData: m,
                                        })
                                      }
                                    >
                                      {m.metadata?.name || "맵 " + m.phase}
                                    </Button>
                                  ))}
                                  <Button
                                    onClick={() => {
                                      setTab("entities");
                                      setFilter("전체");
                                    }}
                                  >
                                    몬스터 관리
                                  </Button>
                                </Space>
                                <Table
                                  size="small"
                                  pagination={false}
                                  rowKey="entity_code"
                                  scroll={{ x: 800 }}
                                  columns={entityColumns.slice(0, 5)}
                                  dataSource={members.filter(
                                    (e) => e.spawn_room_code === r.room_code,
                                  )}
                                />
                              </Card>
                            ),
                          }))}
                        />
                        {unconfigured.map((code) => (
                          <Card key={code} size="small">
                            <Space>
                              <Text>{nameOf(code)}</Text>
                              <Tag color="orange">대표 보스 미지정</Tag>
                              <Button
                                disabled={!ready}
                                onClick={() =>
                                  open("room", { room_code: code })
                                }
                              >
                                구성하기
                              </Button>
                            </Space>
                          </Card>
                        ))}
                      </Space>
                    ),
                  },
                  {
                    key: "entities",
                    label: "보스·쫄몹",
                    children: (
                      <>
                        <Space wrap style={{ marginBottom: 16 }}>
                          <Input.Search
                            aria-label="레이드 몬스터 검색"
                            placeholder="몬스터 이름 또는 코드 검색"
                            allowClear
                            value={memberSearch}
                            onChange={(e) => setMemberSearch(e.target.value)}
                            style={{ width: 260 }}
                          />
                          <Select
                            value={filter}
                            onChange={setFilter}
                            options={["전체", "보스", "쫄몹", "오브젝트"].map(
                              (v) => ({ value: v, label: v }),
                            )}
                          />
                          <Button
                            type="primary"
                            disabled={!ready}
                            onClick={() => open("entity")}
                          >
                            몬스터·오브젝트 추가
                          </Button>
                          <Select
                            disabled={!ready}
                            style={{ minWidth: 240 }}
                            placeholder="미소속 몬스터를 이 레이드에 연결"
                            showSearch
                            optionFilterProp="label"
                            value={undefined}
                            options={options(
                              entities.filter((e) => !e.spawn_raid_code),
                              "entity_code",
                              "entity_name",
                            )}
                            onChange={(code) =>
                              open(
                                "entity",
                                entities.find((e) => e.entity_code === code),
                              )
                            }
                          />
                        </Space>
                        <Table
                          className="resource-table"
                          loading={loading}
                          rowKey="entity_code"
                          scroll={{ x: 1000 }}
                          columns={entityColumns.filter(
                            (c) => c.dataIndex !== "parent_code",
                          )}
                          expandable={{
                            expandedRowRender: (e: RaidEntity) => (
                              <div className="resource-details">
                                <Text code>{e.entity_code}</Text>
                                <p>
                                  관련 보스:{" "}
                                  {entities.find(
                                    (parent) =>
                                      parent.entity_code === e.parent_code,
                                  )?.entity_name ||
                                    e.parent_code ||
                                    "미지정"}
                                </p>
                                <Text>
                                  {e.description || "등록된 설명이 없습니다."}
                                </Text>
                              </div>
                            ),
                          }}
                          dataSource={members.filter(
                            (e) =>
                              (filter === "전체" ||
                                entityCategory(e) === filter) &&
                              `${e.entity_name} ${e.entity_code}`
                                .toLowerCase()
                                .includes(memberSearch.toLowerCase()),
                          )}
                          rowSelection={{
                            selectedRowKeys: compared,
                            onChange: setCompared,
                            getCheckboxProps: (e) => ({
                              disabled: entityCategory(e) === "오브젝트",
                            }),
                          }}
                        />
                        {compared.length > 0 && (
                          <Button onClick={() => setTab("compare")}>
                            선택한 몬스터 비교
                          </Button>
                        )}
                      </>
                    ),
                  },
                  {
                    key: "compare",
                    label: "능력치 비교",
                    children: comparison,
                  },
                ]}
              />
            </>
          ) : (
            <Empty
              description={loading ? "불러오는 중" : "레이드를 추가해주세요"}
            />
          )}
        </Col>
      </Row>
      <Modal
        open={!!editor}
        title={
          editor?.kind === "raid"
            ? "레이드 편집"
            : editor?.kind === "room"
              ? "룸과 대표 보스"
              : "몬스터·오브젝트 편집"
        }
        onCancel={() => setEditor(null)}
        onOk={save}
        confirmLoading={saving}
        width={760}
        okText="저장"
        cancelText="취소"
        forceRender
      >
        <Form form={form} layout="vertical">
          {editor?.kind === "raid" && (
            <>
              <Form.Item name="raid_code" label="레이드 코드" rules={required}>
                <Input disabled={!!editor.record} maxLength={50} />
              </Form.Item>
              <Form.Item name="raid_name" label="레이드 이름" rules={required}>
                <Input maxLength={100} />
              </Form.Item>
              <Form.Item
                name="raid_room_code"
                label="입장 룸 (룸 구성 후 지정)"
              >
                <Select
                  allowClear
                  options={raidRooms.map((r) => ({
                    value: r.room_code,
                    label: nameOf(r.room_code),
                  }))}
                />
              </Form.Item>
              <Space>
                <Form.Item
                  name="min_entry_level"
                  label="최소 입장 레벨"
                  rules={required}
                >
                  <InputNumber min={1} precision={0} />
                </Form.Item>
                <Form.Item name="dungeon_x" label="던전 X" rules={required}>
                  <InputNumber min={0} precision={0} />
                </Form.Item>
                <Form.Item name="dungeon_y" label="던전 Y" rules={required}>
                  <InputNumber min={0} precision={0} />
                </Form.Item>
              </Space>
            </>
          )}
          {editor?.kind === "room" && (
            <>
              <Alert
                type="info"
                message="대표 보스는 한 명 이상 필요합니다. 선택한 보스는 이 룸에 배치됩니다."
              />
              {!editor.record && (
                <Form.Item label="기존 장소 불러오기 (선택)">
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={options(rooms, "room_code", "room_name")}
                    onChange={(code) => {
                      const room = rooms.find((r) => r.room_code === code);
                      if (room) form.setFieldsValue(room);
                    }}
                  />
                </Form.Item>
              )}
              <Form.Item name="room_code" label="룸 코드" rules={required}>
                <Input disabled={!!editor.record} maxLength={50} />
              </Form.Item>
              <Form.Item name="room_name" label="룸 이름" rules={required}>
                <Input maxLength={100} />
              </Form.Item>
              <Form.Item name="description" label="장소 설명">
                <Input.TextArea />
              </Form.Item>
              <Form.Item name="room_order" label="룸 순서" rules={required}>
                <InputNumber min={1} precision={0} />
              </Form.Item>
              <Form.Item
                name="representative_codes"
                label="대표 보스 (보상 연결 대상)"
                rules={[
                  {
                    required: true,
                    type: "array",
                    min: 1,
                    message: "대표 보스를 한 명 이상 지정해주세요.",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  options={options(
                    members.filter(isBoss),
                    "entity_code",
                    "entity_name",
                  )}
                />
              </Form.Item>
            </>
          )}
          {editor?.kind === "entity" && (
            <>
              <Form.Item
                name="entity_code"
                label="몬스터 코드"
                rules={required}
              >
                <Input disabled={!!editor.record} maxLength={50} />
              </Form.Item>
              <Form.Item name="entity_name" label="이름" rules={required}>
                <Input maxLength={100} />
              </Form.Item>
              <Form.Item name="entity_type" label="구분" rules={required}>
                <Select
                  options={[
                    { value: "BOSS", label: "보스" },
                    { value: "BOSS_DOUBLE", label: "보스 (동시 전투)" },
                    { value: "BOSS_PHASE", label: "보스 (페이즈)" },
                    { value: "SUMMON", label: "쫄몹 / 소환 몬스터" },
                    { value: "TRAP", label: "장판·함정" },
                    { value: "OBJECT", label: "오브젝트" },
                    { value: "DUMMY", label: "더미" },
                    { value: "NPC", label: "NPC" },
                  ]}
                />
              </Form.Item>
              <Form.Item name="spawn_room_code" label="출현 룸">
                <Select
                  allowClear
                  options={raidRooms.map((r) => ({
                    value: r.room_code,
                    label: nameOf(r.room_code),
                  }))}
                />
              </Form.Item>
              <Form.Item name="parent_code" label="관련 보스 (선택)">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={options(
                    members.filter(
                      (e) =>
                        isBoss(e) &&
                        e.entity_code !== editor.record?.entity_code,
                    ),
                    "entity_code",
                    "entity_name",
                  )}
                />
              </Form.Item>
              <Space wrap>
                <Form.Item name="max_hp" label="HP">
                  <InputNumber min={1} />
                </Form.Item>
                <Form.Item
                  name="phase_count"
                  label="페이즈 수"
                  rules={required}
                >
                  <InputNumber min={1} precision={0} />
                </Form.Item>
                <Form.Item name="size_tiles" label="크기 (타일)">
                  <InputNumber min={1} />
                </Form.Item>
                <Form.Item name="spawn_order" label="출현 순서">
                  <InputNumber min={1} precision={0} />
                </Form.Item>
              </Space>
              <Form.Item name="description" label="설명">
                <Input.TextArea />
              </Form.Item>
              <Form.Item name="balance_status" label="능력치 검토 상태">
                <Select
                  options={[
                    { value: "unreviewed", label: "미검토" },
                    { value: "draft", label: "작성 중" },
                    {
                      value: "approved",
                      label: "검토 완료 (HP와 모든 능력치 필요)",
                    },
                  ]}
                />
              </Form.Item>
              <Form.Item name="balance_note" label="능력치 메모">
                <Input.TextArea />
              </Form.Item>
              <Collapse
                key={editor.record?.entity_code || "new"}
                items={[
                  {
                    key: "stats",
                    label: "능력치 상세 설정",
                    forceRender: true,
                    children: (
                      <>
                        <Text type="secondary">
                          게임 데이터에서 확인한 수치를 입력합니다. 미입력 값은
                          비교표에 미입력으로 표시됩니다.
                        </Text>
                        {Object.entries(combatStats)
                          .filter(([, value]) => value !== null)
                          .map(([key, value]) => (
                            <Space
                              key={key}
                              style={{ display: "flex", marginTop: 8 }}
                            >
                              <Text>
                                {entityStats.find((s) => s.key === key)?.name ||
                                  key}
                              </Text>
                              <InputNumber
                                aria-label={key}
                                min={0}
                                precision={
                                  [
                                    "stat_str",
                                    "stat_dex",
                                    "stat_vit",
                                    "stat_int",
                                    "stat_foc",
                                    "stat_wiz",
                                  ].includes(key)
                                    ? 0
                                    : undefined
                                }
                                value={value}
                                onChange={(v) => {
                                  if (v !== null)
                                    setCombatStats({
                                      ...combatStats,
                                      [key]: v,
                                    });
                                }}
                              />
                              <Button
                                onClick={() => {
                                  const next = { ...combatStats };
                                  next[key] = null;
                                  setCombatStats(next);
                                }}
                              >
                                제거
                              </Button>
                            </Space>
                          ))}
                        <Space style={{ marginTop: 12 }}>
                          <Select
                            style={{ width: 260 }}
                            placeholder="능력치 선택"
                            showSearch
                            optionFilterProp="label"
                            value={newStat}
                            onChange={setNewStat}
                            options={entityStats
                              .filter((s) => combatStats[s.key] == null)
                              .map((s) => ({ value: s.key, label: s.name }))}
                          />
                          <Button
                            disabled={!newStat}
                            onClick={() => {
                              if (newStat)
                                setCombatStats({
                                  ...combatStats,
                                  [newStat]: 0,
                                });
                              setNewStat(undefined);
                            }}
                          >
                            추가
                          </Button>
                        </Space>
                      </>
                    ),
                  },
                ]}
              />
            </>
          )}
        </Form>
      </Modal>
      {map && (
        <SimpleMapModal
          visible
          {...map}
          onClose={() => setMap(null)}
          onSave={() => {
            setMap(null);
            load();
          }}
        />
      )}
    </div>
  );
};
export default RaidWorkspace;
